import { useState } from 'react';
import { User, Calendar, Pencil, Archive, Loader2, Save, X, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ClientNote } from '@/api/client-notes';
import { useUpdateNote, useArchiveNote, useUploadImages, useDeleteImage } from '../hooks/useClientNotes';
import { ImageViewer } from './ImageViewer';
import { ImageCapture } from './ImageCapture';
import { NoteImage } from './NoteImage';

interface NoteDetailSheetProps {
  note: ClientNote | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatEmployeeName(employee: { firstName: string; lastName: string }): string {
  return `${employee.lastName} ${employee.firstName.charAt(0)}.`;
}

export function NoteDetailSheet({ note, open, onOpenChange }: NoteDetailSheetProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const updateNote = useUpdateNote();
  const archiveNote = useArchiveNote();
  const uploadImages = useUploadImages();
  const deleteImage = useDeleteImage();

  const handleOpenViewer = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const handleAddFiles = (files: File[]) => {
    if (!note) return;
    // Upload immediately
    uploadImages.mutate({ clientId: note.clientId, noteId: note.id, files });
  };

  const handleDeleteImage = (imageId: string) => {
    if (!note) return;
    deleteImage.mutate({ clientId: note.clientId, noteId: note.id, imageId });
  };

  const handleStartEdit = () => {
    if (note) {
      setEditText(note.text);
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText('');
  };

  const handleSave = () => {
    if (!note || !editText.trim()) return;

    updateNote.mutate(
      { clientId: note.clientId, noteId: note.id, text: editText.trim() },
      {
        onSuccess: () => {
          setIsEditing(false);
          setEditText('');
          onOpenChange(false);
        },
      }
    );
  };

  const handleArchive = () => {
    if (!note) return;

    archiveNote.mutate(
      { clientId: note.clientId, noteId: note.id },
      {
        onSuccess: () => {
          setShowArchiveDialog(false);
          onOpenChange(false);
        },
      }
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setIsEditing(false);
      setEditText('');
    }
    onOpenChange(newOpen);
  };

  if (!note) return null;

  const wasEdited = note.updatedBy !== null && note.updatedAt !== note.createdAt;

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="bottom" className="h-[70vh] flex flex-col">
          <SheetHeader className="flex-shrink-0">
            <SheetTitle>Заметка</SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {/* Note text */}
            {isEditing ? (
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="min-h-[120px]"
                autoFocus
              />
            ) : (
              <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/30">
                <p className="text-sm whitespace-pre-wrap">{note.text}</p>
              </div>
            )}

            {/* Images */}
            {note.images.length > 0 && (
              <div className="space-y-2">
                {note.images.map((image, index) => (
                  <div key={image.id} className="relative">
                    <button
                      type="button"
                      onClick={() => handleOpenViewer(index)}
                      className="w-full rounded-lg overflow-hidden border dark:border-gray-700"
                    >
                      <NoteImage
                        clientId={note.clientId}
                        noteId={note.id}
                        imageId={image.id}
                        alt={image.originalName}
                        className="w-full h-auto"
                      />
                    </button>
                    {isEditing && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={() => handleDeleteImage(image.id)}
                        disabled={deleteImage.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add more images button (in edit mode) */}
            {isEditing && (
              <div className="flex items-center gap-2">
                <ImageCapture onCapture={handleAddFiles} disabled={uploadImages.isPending} />
                <span className="text-sm text-muted-foreground">
                  {uploadImages.isPending ? 'Загрузка...' : 'Добавить фото'}
                </span>
              </div>
            )}

            {/* Metadata */}
            {!isEditing && (
              <div className="space-y-3 text-sm">
                {/* Created by */}
                <div className="flex items-start gap-2 text-muted-foreground">
                  <User className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-medium">Создал:</span>{' '}
                    {formatEmployeeName(note.createdBy)}
                  </div>
                </div>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>{formatDateTime(note.createdAt)}</div>
                </div>

                {/* Updated by (if was edited) */}
                {wasEdited && note.updatedBy && (
                  <>
                    <div className="border-t pt-3" />
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Pencil className="h-4 w-4 mt-0.5 shrink-0" />
                      <div>
                        <span className="font-medium">Изменил:</span>{' '}
                        {formatEmployeeName(note.updatedBy)}
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4 mt-0.5 shrink-0" />
                      <div>{formatDateTime(note.updatedAt)}</div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex-shrink-0 pt-4 border-t">
            {isEditing ? (
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={handleSave}
                  disabled={updateNote.isPending || !editText.trim()}
                >
                  {updateNote.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Сохранить
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={updateNote.isPending}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleStartEdit}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Редактировать
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setShowArchiveDialog(true)}
                >
                  <Archive className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Archive confirmation dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Архивировать заметку?</AlertDialogTitle>
            <AlertDialogDescription>
              Заметка будет скрыта из списка. Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={archiveNote.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {archiveNote.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Archive className="h-4 w-4 mr-2" />
              )}
              Архивировать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image viewer */}
      {note.images.length > 0 && (
        <ImageViewer
          clientId={note.clientId}
          noteId={note.id}
          images={note.images}
          initialIndex={viewerIndex}
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </>
  );
}
